/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { Component } from 'react';
import { array, arrayOf, bool, func, shape, string, number, oneOf } from 'prop-types';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { findOptionsForSelectFilter } from '../../util/search';
import { LISTING_STATE_PENDING_APPROVAL, LISTING_STATE_CLOSED, propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
} from '../../util/urlHelpers';
import { formatMoney } from '../../util/currency';
import { createResourceLocatorString } from '../../util/routes';
import { richTextToMetaDescription } from '../../util/seo';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/UI.duck';
import {
  Page,
  NamedRedirect,
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Footer,
  Modal,
  ModalBlockedUser,
} from '../../components';
import { hasVerifiedMark } from '../../components/VerifiedUserMark/VerifiedUserMark';
import { TopbarContainer, NotFoundPage } from '../../containers';
import { EnquiryForm } from '../../forms';

import { sendEnquiry, loadData, setInitialValues, publishListingDraft } from './ListingPage.duck';
import ListingOwnerBanner from './ListingOwnerBanner';
import {
  faqSchema,
  ownerBanner,
  ownVariantRedirect,
  packageOffersSchema,
  relatedListingsForPage,
  reviewSchemaFields,
  sellerSchema,
  summarizeReviews,
} from './ListingPage.helpers';
import {
  getListingCompleteness,
  listingToCompletenessValues,
} from '../../util/listingCompleteness';
import {
  PRICING_MODEL_HOURLY,
  PRICING_MODEL_MONTHLY,
  getListingFaq,
  getListingPackages,
  priceUnitMessageId,
} from '../../util/listingPackages';
import SectionImages from './SectionImages';
import SectionSellerBar from './SectionSellerBar';
import SectionAbout from './SectionAbout';
import SectionPackages from './SectionPackages';
import SectionReviews from './SectionReviews';
import SectionFaq from './SectionFaq';
import SectionRelatedWork from './SectionRelatedWork';
import SectionMoreServices from './SectionMoreServices';

import {
  uploadAttachment,
  removeAttachment,
  clearStagedAttachments,
  stagedAttachmentsList,
  readyAttachmentFileIds,
  SCOPE_LISTING_ENQUIRY,
} from '../../ducks/messageAttachments.duck';
import css from './ListingPage.css';
import { updateFavoriteListings } from '../../ducks/user.duck';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const TITLE_UNIT_PRICING_MODELS = [PRICING_MODEL_HOURLY, PRICING_MODEL_MONTHLY];

// The listing price (package 1) in the page title. Hourly and monthly prices
// carry their unit, as on the cards. Project prices don't, so old listings
// keep their titles.
const formatTitlePrice = (price, pricingModel, intl) => {
  if (price && price.currency === config.currency) {
    const formattedPrice = formatMoney(intl, price);
    return TITLE_UNIT_PRICING_MODELS.includes(pricingModel)
      ? `${formattedPrice} ${intl.formatMessage({ id: priceUnitMessageId(pricingModel) })}`
      : formattedPrice;
  }
  return price ? `(${price.currency})` : null;
};

export class ListingPageComponent extends Component {
  constructor(props) {
    super(props);
    const { enquiryModalOpenForListingId, enquiryPackage, params } = props;
    this.state = {
      pageClassNames: [],
      imageCarouselOpen: false,
      enquiryModalOpen: enquiryModalOpenForListingId === params.id,
      enquiryPackage: enquiryModalOpenForListingId === params.id ? enquiryPackage : null,
      blockedModalOpen: false,
    };

    this.onContactUser = this.onContactUser.bind(this);
    this.onSubmitEnquiry = this.onSubmitEnquiry.bind(this);
    this.onPublishDraft = this.onPublishDraft.bind(this);
    this.toggleReviews = this.showRestOfReviews.bind(this);
  }

  showRestOfReviews() {
    this.setState({
      toggleRestOfReviews: !this.state.toggleRestOfReviews,
    });
  }

  async onPublishDraft(listing, listingSlug) {
    const { history, currentUser, onPublishDraft } = this.props;
    const published = await onPublishDraft(listing.id, currentUser);
    // The thunk resolves to nothing when publishing failed.
    if (!published) {
      return;
    }
    const routeParams = { id: listing.id.uuid, slug: listingSlug };
    // Replace the draft URL so Back skips it.
    history.replace(
      published.attributes.state === LISTING_STATE_PENDING_APPROVAL
        ? createResourceLocatorString(
            'ListingPageVariant',
            routeConfiguration(),
            { ...routeParams, variant: LISTING_PAGE_PENDING_APPROVAL_VARIANT },
            {}
          )
        : createResourceLocatorString('ListingPage', routeConfiguration(), routeParams, {})
    );
  }

  onContactUser(openModal = true, enquiryPackage = null) {
    const { currentUser, history, callSetInitialValues, params, location } = this.props;

    const isUserBlocked = currentUser?.attributes?.profile?.metadata?.spamEmailDetected === true;

    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };

      // We need to log in before showing the modal, but first we need to ensure
      // that modal does open when user is redirected back to this listingpage
      openModal &&
        callSetInitialValues(setInitialValues, {
          enquiryModalOpenForListingId: params.id,
          enquiryPackage,
        });

      // signup and return back to listingPage.
      history.push(createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}), state);
    } else if (isUserBlocked) {
      this.setState({ blockedModalOpen: true });
    } else {
      this.setState({ enquiryModalOpen: true, enquiryPackage });
    }
  }

  onSubmitEnquiry(values) {
    const {
      history,
      params,
      onSendEnquiry,
      getListing,
      getOwnListing,
      currentUser,
      stagedAttachments,
      onClearAttachments,
    } = this.props;
    const routes = routeConfiguration();
    const listingId = new UUID(params.id);
    const { message } = values;
    const isPendingApprovalVariant = params.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = params.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));
    const author = currentListing && currentListing.author;
    const isLightEntrepreneur =
      author &&
      author.attributes &&
      author.attributes.profile.publicData &&
      author.attributes.profile.publicData.account_role === 'light-entrepreneur';
    const listingTitle = currentListing.attributes.title;
    const { enquiryPackage } = this.state;

    onSendEnquiry(
      listingId,
      isLightEntrepreneur,
      message.trim(),
      currentUser,
      listingTitle,
      readyAttachmentFileIds(stagedAttachments),
      enquiryPackage
    )
      .then(txId => {
        onClearAttachments();
        this.setState({ enquiryModalOpen: false, enquiryPackage: null });

        if (typeof window === 'object') {
          window.dataLayer.push({
            event: 'contact_author',
            content_type: 'service',
            ecommerce: {
              items: [
                {
                  item_name: currentListing.attributes.title,
                  item_id: currentListing.id.uuid,
                  price: 0,
                  item_brand: currentListing.author.attributes.profile.displayName,
                  item_category: currentListing.attributes.publicData.category,
                  item_list_name: 'listing_page',
                },
              ],
            },
            ...(enquiryPackage
              ? {
                  enquiryPackageTitle: enquiryPackage.title,
                  enquiryPackagePricingModel: enquiryPackage.pricingModel,
                }
              : {}),
          });
        }

        // Redirect to OrderDetailsPage
        history.push(
          createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {})
        );
      })
      .catch(() => {
        // Ignore, error handling in duck file
      });
  }

  render() {
    const {
      isAuthenticated,
      currentUser,
      getListing,
      getOwnListing,
      intl,
      onManageDisableScrolling,
      params: rawParams,
      location,
      scrollingDisabled,
      showListingError,
      sendEnquiryInProgress,
      sendEnquiryError,
      stagedAttachments,
      onUploadAttachment,
      onRemoveAttachment,
      onClearAttachments,
      filterConfig,
      onupdateFavoriteListings,
      favoriteListings,
      otherListings,
      publishInProgress,
      publishError,
      reviews,
      offPlatformReviews,
    } = this.props;

    // The freelancer's reviews with their average and count, computed once
    // for the seller bar, SectionReviews and the JSON-LD.
    const reviewsSummary = summarizeReviews(reviews, offPlatformReviews);

    const listingId = new UUID(rawParams.id);
    const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
    const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
    const currentListing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId));

    const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
    const params = { slug: listingSlug, ...rawParams };

    // An approved listing on /pending-approval, a published one on /draft
    // (Back after publishing), or someone else's listing on either (the
    // own-listing fetch fails with 403) moves to the matching URL.
    const variantRedirect = ownVariantRedirect({
      variant: rawParams.variant,
      listingState: currentListing.id ? currentListing.attributes.state : null,
      errorStatus: showListingError ? showListingError.status : null,
    });

    if (variantRedirect) {
      const redirectParams = variantRedirect.variant
        ? { ...params, variant: variantRedirect.variant }
        : params;
      return (
        <NamedRedirect
          name={variantRedirect.name}
          params={redirectParams}
          search={location.search}
        />
      );
    }

    const { description = '', price = null, title = '' } = currentListing.attributes;
    const descriptionText = currentListing.attributes.publicData.descriptionText;
    const publicData = currentListing.attributes.publicData || {};

    const userPublicData =
      (currentUser &&
        currentUser.attributes &&
        currentUser.attributes.profile &&
        currentUser.attributes.profile.publicData) ||
      {};
    const accountRole = userPublicData.account_role;
    const isProvider = ['freelancer', 'light-entrepreneur'].includes(accountRole);

    const richTitle = (
      <span>
        {richText(title, {
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
          longWordClass: css.longWord,
        })}
      </span>
    );

    const topbar = <TopbarContainer currentPage="ListingPage" />;

    if (showListingError && showListingError.status === 404) {
      // 404 listing not found

      return <NotFoundPage />;
    } else if (showListingError) {
      console.error('Error loading service listing page');
      console.error(showListingError);
      // Other error in fetching listing

      const errorTitle = intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      });

      return (
        <Page title={errorTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.errorText}>
                <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    } else if (!currentListing.id) {
      // Still loading the listing

      const loadingTitle = intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      });

      return (
        <Page title={loadingTitle} scrollingDisabled={scrollingDisabled}>
          <LayoutSingleColumn className={css.pageRoot}>
            <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
            <LayoutWrapperMain>
              <p className={css.loadingText}>
                <FormattedMessage id="ListingPage.loadingListingMessage" />
              </p>
            </LayoutWrapperMain>
            <LayoutWrapperFooter>
              <Footer />
            </LayoutWrapperFooter>
          </LayoutSingleColumn>
        </Page>
      );
    }

    const handleViewPhotosClick = e => {
      // Stop event from bubbling up to prevent image click handler
      // trying to open the carousel as well.
      e.stopPropagation();
      this.setState({
        imageCarouselOpen: true,
      });
    };
    const authorAvailable = currentListing && currentListing.author;
    const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
    const isOwnListing =
      userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;
    const showContactUser =
      authorAvailable && (!currentUser || (currentUser && !isOwnListing)) && !isProvider;
    const ownBanner = isOwnListing
      ? ownerBanner({
          listingState: currentListing.attributes.state,
          listingId: listingId.uuid,
          slug: listingSlug,
        })
      : null;

    const currentAuthor = authorAvailable ? currentListing.author : null;
    const ensuredAuthor = ensureUser(currentAuthor);

    const noIndexing = currentListing.attributes.publicData?.noIndexing || false;

    // When user is banned or deleted the listing is also deleted.
    // Because listing can be never showed with banned or deleted user we don't have to provide
    // banned or deleted display names for the function
    const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

    // Read through the adapter so old listings keep package 1
    // (attributes.price) plus packages 2–5.
    const packages = getListingPackages(currentListing);
    const formattedPrice = formatTitlePrice(price, packages[0] && packages[0].pricingModel, intl);

    const listingImages = (listing, variantName) =>
      (listing.images || [])
        .map(image => {
          const variants = image.attributes.variants;
          const variant = variants ? variants[variantName] : null;

          // deprecated
          // for backwards combatility only
          const sizes = image.attributes.sizes;
          const size = sizes ? sizes.find(i => i.name === variantName) : null;

          return variant || size;
        })
        .filter(variant => variant != null);

    const facebookImages = listingImages(currentListing, 'facebook');
    const twitterImages = listingImages(currentListing, 'twitter');
    const siteTitle = config.siteTitle;
    // const checkThis = publicData?.priceArray ? publicData?.priceArray[0]?._packagePrice : price;
    // const money = new Money(checkThis?.amount || checkThis, config.currency);
    // const formattedPrice = formatMoney(intl, money);
    const schemaTitle = intl.formatMessage(
      { id: 'ListingPage.schemaTitle' },
      {
        title,
        price: formattedPrice || 0,
        siteTitle,
      }
    );
    const schemaDescriptionPlaceholder = intl.formatMessage(
      { id: 'ListingPage.schemaDescriptionPlaceholder' },
      {
        title,
      }
    );

    const canonicalURL = `${process.env.REACT_APP_CANONICAL_ROOT_URL}/l/${createSlug(title)}/${
      currentListing.id.uuid
    }`;

    const metaDescription = descriptionText
      ? richTextToMetaDescription(descriptionText)
      : description;

    const offers = packageOffersSchema({
      packages,
      listingTitle: title,
      currency: config.currency,
      url: canonicalURL,
      isClosed: currentListing.attributes.state === LISTING_STATE_CLOSED,
      seller: sellerSchema({
        name: authorDisplayName,
        profileUrl: ensuredAuthor.id
          ? `${process.env.REACT_APP_CANONICAL_ROOT_URL}/u/${ensuredAuthor.id.uuid}`
          : null,
      }),
    });
    // The @id is the listing URL, the same as this listing's Service node in
    // the author's ProfilePage schema.
    const schema = {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      '@id': canonicalURL,
      name: title,
      url: canonicalURL,
      ...(facebookImages.length > 0 && { image: facebookImages.map(image => image.url) }),
      description: metaDescription || schemaDescriptionPlaceholder,
      ...reviewSchemaFields(reviewsSummary),
      ...(offers.length > 0 && { offers }),
    };

    // The author's other listings (every published listing, also on the
    // owner's draft view, newest first): the works and services picked for
    // this listing, or the newest ones, for "Related work" and "More services".
    const {
      works: relatedWorks,
      services: moreServices,
      serviceCount: moreServicesCount,
    } = relatedListingsForPage({ listing: currentListing, authorListings: otherListings });

    const categoryOptions = findOptionsForSelectFilter('category', filterConfig);

    // Category option labels are FormattedMessage elements; schema needs plain text
    const categoryOptionText = option =>
      option?.label?.props?.id ? intl.formatMessage({ id: option.label.props.id }) : option?.key;

    const categoryOption = categoryOptions.find(c => c.key === publicData.category);
    const parentCategoryOption = categoryOption?.parent
      ? categoryOptions.find(c => c.key === categoryOption.parent)
      : null;

    const breadcrumbs = [
      { name: siteTitle, url: config.canonicalRootURL },
      ...(parentCategoryOption?.path
        ? [
            {
              name: categoryOptionText(parentCategoryOption),
              url: `${config.canonicalRootURL}${parentCategoryOption.path}`,
            },
          ]
        : []),
      ...(categoryOption?.path
        ? [
            {
              name: categoryOptionText(categoryOption),
              url: `${config.canonicalRootURL}${categoryOption.path}`,
            },
          ]
        : []),
      { name: title, url: canonicalURL },
    ];

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: crumb.url,
      })),
    };

    if (currentListing.attributes.publicData.listingType === 'project') {
      return (
        <NamedRedirect
          name="ProjectListingPage"
          params={{
            slug: createSlug(currentListing.attributes.title || ''),
            id: currentListing.id.uuid,
          }}
          search={location.search}
        />
      );
    }

    return (
      <Page
        title={schemaTitle}
        scrollingDisabled={scrollingDisabled}
        author={authorDisplayName}
        contentType="website"
        description={metaDescription}
        facebookImages={facebookImages}
        twitterImages={twitterImages}
        schema={[
          schema,
          breadcrumbSchema,
          faqSchema({ faq: getListingFaq(currentListing), url: canonicalURL }),
        ]}
        listingCanonicalURL={canonicalURL}
        noIndexing={noIndexing}
      >
        <LayoutSingleColumn className={css.pageRoot}>
          <LayoutWrapperTopbar>{topbar}</LayoutWrapperTopbar>
          <LayoutWrapperMain>
            {ownBanner ? (
              <ListingOwnerBanner
                variant={ownBanner.variant}
                editParams={ownBanner.editParams}
                completeness={
                  ownBanner.variant === 'draft'
                    ? getListingCompleteness(listingToCompletenessValues(currentListing))
                    : null
                }
                onPublish={() => this.onPublishDraft(currentListing, listingSlug)}
                publishInProgress={publishInProgress}
                publishError={publishError}
              />
            ) : null}
            <div>
              <div className={css.contentContainer}>
                <div className={css.mainContent}>
                  <div className={css.listingHeader}>
                    <h1 className={css.title}>{richTitle}</h1>
                    <SectionSellerBar
                      author={ensuredAuthor}
                      reviewsSummary={reviewsSummary}
                      showContactUser={showContactUser}
                      onContactUser={this.onContactUser}
                      currentUser={currentUser}
                      listing={currentListing}
                      favoriteListings={favoriteListings}
                      updateFavoriteListings={onupdateFavoriteListings}
                    />
                    <SectionImages
                      title={title}
                      listing={currentListing}
                      isOwnListing={isOwnListing}
                      imageCarouselOpen={this.state.imageCarouselOpen}
                      onImageCarouselClose={() => this.setState({ imageCarouselOpen: false })}
                      handleViewPhotosClick={handleViewPhotosClick}
                      onManageDisableScrolling={onManageDisableScrolling}
                    />
                  </div>
                  <SectionAbout
                    descriptionText={descriptionText}
                    description={description}
                    authorVerified={hasVerifiedMark(ensuredAuthor)}
                    showContactUser={showContactUser}
                    onContactUser={this.onContactUser}
                    onManageDisableScrolling={onManageDisableScrolling}
                  />
                  <SectionPackages
                    listing={currentListing}
                    showContactUser={showContactUser}
                    onContactUser={this.onContactUser}
                  />
                  <SectionReviews
                    reviews={reviewsSummary.reviews}
                    average={reviewsSummary.average}
                    count={reviewsSummary.count}
                    authorId={ensuredAuthor.id?.uuid}
                  />
                  <SectionFaq listing={currentListing} />
                  <SectionRelatedWork
                    works={relatedWorks}
                    authorId={ensuredAuthor.id?.uuid}
                    authorName={authorDisplayName}
                  />
                  <SectionMoreServices
                    services={moreServices}
                    totalCount={moreServicesCount}
                    author={ensuredAuthor}
                  />
                </div>
              </div>
            </div>
            <Modal
              id="ListingPage.enquiry"
              contentClassName={css.enquiryModalContent}
              isOpen={!isProvider && isAuthenticated && this.state.enquiryModalOpen}
              onClose={() => {
                onClearAttachments();
                this.setState({ enquiryModalOpen: false });
              }}
              usePortal
              containerClassName={css.enquiryModalContainer}
              onManageDisableScrolling={onManageDisableScrolling}
            >
              <EnquiryForm
                className={css.enquiryForm}
                listingTitle={title}
                authorDisplayName={authorDisplayName}
                author={ensuredAuthor}
                enquiryPackage={this.state.enquiryPackage}
                sendEnquiryError={sendEnquiryError}
                onSubmit={this.onSubmitEnquiry}
                inProgress={sendEnquiryInProgress}
                listing={currentListing}
                stagedAttachments={stagedAttachments}
                onUploadAttachment={onUploadAttachment}
                onRemoveAttachment={onRemoveAttachment}
              />
            </Modal>
            <ModalBlockedUser
              isOpen={this.state.blockedModalOpen}
              onClose={() => this.setState({ blockedModalOpen: false })}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </LayoutWrapperMain>
          <LayoutWrapperFooter>
            <Footer />
          </LayoutWrapperFooter>
        </LayoutSingleColumn>
      </Page>
    );
  }
}

ListingPageComponent.defaultProps = {
  currentUser: null,
  enquiryModalOpenForListingId: null,
  enquiryPackage: null,
  showListingError: null,
  reviews: [],
  offPlatformReviews: [],
  fetchReviewsError: null,
  sendEnquiryError: null,
  filterConfig: config.custom.filters,
};

ListingPageComponent.propTypes = {
  // from withRouter
  history: shape({
    push: func.isRequired,
    replace: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  enquiryModalOpenForListingId: string,
  enquiryPackage: shape({
    title: string,
    pricingModel: string,
    price: shape({ amount: number, currency: string }),
  }),
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  offPlatformReviews: array,
  fetchReviewsError: propTypes.error,
  sendEnquiryInProgress: bool.isRequired,
  sendEnquiryError: propTypes.error,
  onSendEnquiry: func.isRequired,
  onPublishDraft: func.isRequired,
  publishInProgress: bool,
  publishError: propTypes.error,
  stagedAttachments: array.isRequired,
  onUploadAttachment: func.isRequired,
  onRemoveAttachment: func.isRequired,
  onClearAttachments: func.isRequired,
  filterConfig: array,
  onupdateFavoriteListings: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.Auth;
  const {
    showListingError,
    reviews,
    fetchReviewsError,
    sendEnquiryInProgress,
    sendEnquiryError,
    enquiryModalOpenForListingId,
    enquiryPackage,
    userListingRefs,
    offPlatformReviews,
    queryOffPlatformReviewsError,
    publishInProgress,
    publishError,
  } = state.ListingPage;
  const { currentUser } = state.user;
  const otherListings = getMarketplaceEntities(state, userListingRefs);

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);

    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);

    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    enquiryModalOpenForListingId,
    enquiryPackage,
    showListingError,
    reviews,
    fetchReviewsError,
    sendEnquiryInProgress,
    sendEnquiryError,
    stagedAttachments: stagedAttachmentsList(state, SCOPE_LISTING_ENQUIRY),
    favoriteListings: state.user.favoriteListingsIds,
    otherListings,
    offPlatformReviews,
    queryOffPlatformReviewsError,
    publishInProgress,
    publishError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onSendEnquiry: (
    listingId,
    isLightEntrepreneur,
    message,
    currentUser,
    listingTitle,
    fileIds,
    enquiryPackage
  ) =>
    dispatch(
      sendEnquiry(
        listingId,
        isLightEntrepreneur,
        message,
        currentUser,
        listingTitle,
        fileIds,
        enquiryPackage
      )
    ),
  onUploadAttachment: file => dispatch(uploadAttachment(SCOPE_LISTING_ENQUIRY, file)),
  onRemoveAttachment: tempId => dispatch(removeAttachment(SCOPE_LISTING_ENQUIRY, tempId)),
  onClearAttachments: () => dispatch(clearStagedAttachments(SCOPE_LISTING_ENQUIRY)),
  onupdateFavoriteListings: params => dispatch(updateFavoriteListings(params)),
  onPublishDraft: (listingId, currentUser) => dispatch(publishListingDraft(listingId, currentUser)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ListingPageComponent);

ListingPage.setInitialValues = initialValues => setInitialValues(initialValues);
ListingPage.loadData = loadData;

export default ListingPage;
